import { useEffect, useId, useRef, useState } from 'react';

import { focusAfter } from './enterKeyNavigation';
import { useDebounced } from './useDebounced';

/**
 * One row of the dropdown, as facts rather than as markup.
 *
 * <p>
 * <strong>Structured fields, not a `renderRow` prop</strong>, and for the reason `DataView` gives
 * for refusing `renderCard`: a component that lets each caller draw its own rows grows as many
 * presentations as it has callers, and they drift. Here the customer picker, the tailor picker and
 * the item picker are one list with three sets of words in it.
 * </p>
 */
export interface PickerOption {
  /**
   * What identifies this row *to the caller*, and deliberately not a database key.
   *
   * <p>
   * <strong>A string, because one list can draw on two tables.</strong> The tailor picker offers
   * the workshop down the lane and the karigar in the back room together, and those come from
   * `TblVendor` and `TblEmployee` — whose keys are IDENTITY from the same 1,000,000 seed, so vendor
   * 1000004 and employee 1000004 both exist and are different people. A numeric id here would make
   * them one row: React would collide the keys, and the caller reading the choice back would attach
   * the wrong master's row to a garment. So such a caller prefixes (`v:1000004`, `e:1000004`) and
   * unpacks it on the way out. A single-master picker still passes `String(row.id)` and is
   * unaffected.
   * </p>
   */
  id: string;
  /** What the thing is called. The line a person reads first. */
  primary: string;
  /** The line under it — a mobile number, a serial code, a category. What tells two apart. */
  secondary?: string;
  /** A short fact on the right: "new", "3 rentals", "ગુજરાતી". */
  badge?: string;
}

/**
 * The search-and-choose control for anything the database has too many of to list.
 *
 * <p>
 * <strong>Extracted from `CustomerPicker`, which had it right and had it alone.</strong> A customer,
 * a vendor and a design are all "thousands of rows, find one by typing part of a name", and each
 * had grown its own answer: the customer a full picker, the tailor a `<datalist>`, the item a native
 * `<select>` holding every design in the shop. Three shapes for one question, and only one of them
 * was any good on a counter.
 * </p>
 *
 * <p>
 * <strong>The caller owns the query, this owns the interaction.</strong> Hooks cannot be passed in —
 * `useCustomers` and `useVendors` have to be called at the caller — so this raises the debounced
 * text and takes back rows. What it keeps is everything that is the same every time: the debounce,
 * the keyboard, the click-outside, the chosen-card swap, and the focus handoff afterwards.
 * </p>
 */
export function SearchPicker({
  options,
  total,
  loading = false,
  value,
  onSearch,
  onSelect,
  onCreateRequested,
  placeholder,
  createLabel,
  disabled = false,
  autoFocus = false,
}: {
  options: PickerOption[];
  /** How many matched in all, so the panel can say when to narrow rather than scroll. */
  total: number;
  loading?: boolean;
  /** The chosen row, or null. The caller owns it — this never holds a selection of its own. */
  value: PickerOption | null;
  /** Raised with the debounced text, to drive the caller's own query. */
  onSearch: (query: string) => void;
  onSelect: (option: PickerOption | null) => void;
  /** Omit to leave out the create row entirely — a design cannot be invented from a booking. */
  onCreateRequested?: ((typed: string) => void) | undefined;
  placeholder: string;
  /** How the create row reads, given what was typed. */
  createLabel?: ((typed: string) => string) | undefined;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  /**
   * The list is open over an existing choice.
   *
   * <p>
   * Kept apart from `open` because the two answer different questions: `open` is whether the
   * dropdown is showing, `browsing` is whether the caller still has a selection underneath it. That
   * is what lets backing out restore the card instead of leaving the field empty.
   * </p>
   */
  const [browsing, setBrowsing] = useState(false);

  const listId = useId();
  const box = useRef<HTMLDivElement>(null);

  // Where to carry on from once the chosen row has replaced the search box. Held rather than read
  // at the time because the element focus is *in* is one of the things that goes away.
  const resume = useRef<HTMLElement | null>(null);

  const debounced = useDebounced(query);

  useEffect(() => {
    onSearch(debounced);
    // The caller's handler is usually a setState and stable; depending on it would re-run this on
    // every render of a caller that inlines an arrow function, which is most of them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  useEffect(() => {
    setActive(0);
  }, [debounced]);

  /**
   * Return to the chosen card without changing anything.
   *
   * <p>
   * <strong>This is what "Change" used to destroy.</strong> It cleared the selection outright, so
   * opening the list to check a second name and then thinking better of it left the field empty —
   * and on the booking counter that meant re-finding the customer you had already found. Backing
   * out now keeps what was there; the only ways to lose a selection are picking another one or
   * saying so on the selected row.
   * </p>
   */
  const backOut = () => {
    setQuery('');
    setOpen(false);
    setBrowsing(false);
  };

  // Click-outside closes. Pointerdown rather than click, so choosing a row inside still registers
  // before the panel goes — a click listener here races with the row's own handler.
  useEffect(() => {
    if (!open) {
      return;
    }

    const away = (event: PointerEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) {
        backOut();
      }
    };

    document.addEventListener('pointerdown', away);

    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const choose = (option: PickerOption) => {
    // Picking the row that is already chosen means "not this one after all". The list is the only
    // place a selection can be undone, which is why the row says so rather than leaving the reader
    // to guess that clicking it twice does something.
    if (option.id === value?.id) {
      onSelect(null);
      backOut();

      return;
    }

    // Captured before the swap, because `box` is on the search panel and unmounts with it. The
    // parent outlives both states of the picker, which is what makes "the next field after this
    // one" answerable a render later.
    resume.current = box.current?.parentElement ?? null;

    onSelect(option);
    setQuery('');
    setOpen(false);
    setBrowsing(false);
  };

  /**
   * Carry on to the next field once something is chosen.
   *
   * <p>
   * Choosing replaces the search box with a card naming what was picked, so the control that had
   * focus stops existing and focus falls to `<body>` — after which Enter does nothing, Tab starts
   * from the top of the document, and a clerk who was typing has to reach for the mouse.
   * </p>
   *
   * <p>
   * <strong>It runs on the pick, not on the value.</strong> The ref is set only inside `choose`, so
   * a caller restoring a saved draft does not have focus yanked out from under whatever the user
   * was doing.
   * </p>
   */
  useEffect(() => {
    const anchor = resume.current;

    if (!value || !anchor) {
      return;
    }

    resume.current = null;
    focusAfter(anchor);
  }, [value]);

  /* Something is chosen: show what, not a search box they would have to clear to read. */
  if (value && !browsing) {
    return (
      <div className="border-border-color flex items-center gap-3 rounded border bg-white px-3 py-2">
        <span className="bg-primary-transparent text-primary flex size-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold">
          {initials(value.primary)}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{value.primary}</p>
          {value.secondary && (
            <p className="font-mono-nums text-default truncate text-[13px]">{value.secondary}</p>
          )}
        </div>

        <button
          type="button"
          disabled={disabled}
          className="text-default hover:text-dark cursor-pointer text-[13px] disabled:cursor-not-allowed"
          onClick={() => {
            setBrowsing(true);
            setOpen(true);
          }}
        >
          Change
        </button>
      </div>
    );
  }

  const typed = query.trim();

  return (
    <div ref={box} className="relative">
      <span className="text-default pointer-events-none absolute inset-y-0 left-3 flex items-center">
        <i className="icon icon-search text-[15px]" />
      </span>

      <input
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        // Browsing is a deliberate press of "Change", so the cursor goes where the typing has to
        // happen. The input mounts at that moment, which is what makes the plain attribute enough.
        autoFocus={autoFocus || browsing}
        disabled={disabled}
        value={query}
        placeholder={placeholder}
        className="border-border-color disabled:bg-light w-full rounded-md border bg-white py-2 pr-3 pl-9 text-sm text-gray-900 focus:outline-none"
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActive((index) => Math.min(index + 1, Math.max(options.length - 1, 0)));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter') {
            /*
              Swallowed so the page's Enter-to-next-field handler does not move focus out of a
              picker that is mid-choice. `enterKeyNavigation` stands down on `defaultPrevented`
              and on an open dropdown — this reports it through `aria-expanded`, which is the
              same attribute antd's own Select writes.
            */
            const chosen = options[active];

            if (open && chosen) {
              event.preventDefault();
              choose(chosen);
            } else if (open && typed && onCreateRequested) {
              event.preventDefault();
              onCreateRequested(typed);
            }
          } else if (event.key === 'Escape') {
            backOut();
          }
        }}
      />

      {open && (
        <div
          id={listId}
          role="listbox"
          className="border-border-color absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-white shadow-lg"
        >
          {loading && options.length === 0 && (
            <p className="text-default px-3 py-3 text-[13px]">Looking…</p>
          )}

          {!loading && options.length === 0 && (
            <p className="text-default px-3 py-3 text-[13px]">
              {typed ? 'Nothing matches that.' : 'Start typing to search.'}
            </p>
          )}

          {options.map((option, index) => {
            const chosen = option.id === value?.id;

            return (
              <button
                key={option.id}
                type="button"
                role="option"
                /*
                  What is chosen, not what the arrow keys are over. Those are two different facts and
                  this attribute means the first one: a screen reader announcing the highlighted row
                  as "selected" would tell a blind operator they had already picked it. The keyboard
                  position is carried by the background instead, as it was.
                */
                aria-selected={chosen}
                title={chosen ? 'Chosen — click to remove' : undefined}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(option)}
                className={`flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left ${
                  index === active ? 'bg-light' : ''
                }`}
              >
                <span className="bg-primary-transparent text-primary flex size-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold">
                  {initials(option.primary)}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">
                    {option.primary}
                  </span>
                  {option.secondary && (
                    <span className="font-mono-nums text-default block truncate text-[13px]">
                      {option.secondary}
                    </span>
                  )}
                </span>

                {/*
                  Says which row the field is already holding, so reopening the list reads as
                  "this one, unless you say otherwise" rather than as an empty question. It is also
                  the only place a selection can be undone, which is why the tag names the action.
                */}
                {chosen && (
                  <span className="bg-primary-transparent text-primary shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium">
                    Chosen · remove
                  </span>
                )}

                {/*
                  The badge yields before the name does. With `shrink-0` it held its width and
                  truncated the design name instead — "Gold Zari Sherw…" in a narrow column — which is
                  backwards: the name is the identity and the badge is a detail.
                */}
                {option.badge && !chosen && (
                  <span className="text-default max-w-[40%] shrink truncate text-[12px]">
                    {option.badge}
                  </span>
                )}
              </button>
            );
          })}

          {/* Narrowing is faster than scrolling, and this is what says when to narrow. */}
          {total > options.length && (
            <p className="text-default border-border-color border-t px-3 py-2 text-[12px]">
              {total - options.length} more — keep typing to narrow
            </p>
          )}

          {onCreateRequested && (
            <button
              type="button"
              onClick={() => onCreateRequested(typed)}
              className="border-border-color hover:bg-light text-primary w-full cursor-pointer border-t px-3 py-2 text-left text-[13px] font-medium"
            >
              {createLabel ? createLabel(typed) : typed ? `Create “${typed}”` : 'Create a new one'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Up to two letters, for the round badge. A blank name gets a dash rather than an empty circle. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '—';
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
